import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Search, ShieldOff, UserPlus, Users as UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { http } from '../api/client';
import { Badge, StatusBadge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { DataTable, Pagination, type Column } from '../components/ui/DataTable';
import { Input, Select } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { colorFor, dateTime, initials, relativeTime } from '../lib/format';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import type { Page, RoleRow, UserRow } from '../types';

type SortKey = 'full_name' | 'username' | 'created_at' | 'last_login_at' | 'status';

export function UsersPage() {
  const can = useAuth((s) => s.can);
  const toast = useUi((s) => s.toast);
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'full_name', dir: 'asc' });
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [suspending, setSuspending] = useState<UserRow | null>(null);

  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => http.get<{ roles: RoleRow[] }>('/roles'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['users', page, search, status, sort],
    queryFn: () =>
      http.get<Page<UserRow>>('/users', { page, limit: 10, q: search, status, sort: sort.key, dir: sort.dir }),
    placeholderData: keepPreviousData,
  });

  const suspend = useMutation({
    mutationFn: ({ id, to }: { id: string; to: 'suspended' | 'active' }) =>
      http.patch(`/users/${id}`, { status: to }),
    onSuccess: (_r, vars) => {
      toast({
        kind: 'success',
        title: vars.to === 'suspended' ? 'User suspended' : 'User reactivated',
        message: vars.to === 'suspended' ? 'Their sessions were revoked immediately.' : 'They can sign in again.',
      });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => toast({ kind: 'error', title: 'Could not update user', message: (err as Error).message }),
  });

  const columns: Column<UserRow>[] = [
    {
      key: 'full_name', header: 'User', sortable: true,
      render: (u) => (
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: colorFor(u.role_key ?? u.username) }} aria-hidden
          >
            {initials(u.full_name)}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium text-ink">{u.full_name}</span>
            <span className="block truncate text-xs text-ink-faint">
              {u.username}{u.employee_no ? ` · ${u.employee_no}` : ''}
            </span>
          </span>
        </div>
      ),
    },
    {
      key: 'role', header: 'Role',
      render: (u) => <Badge tone="brand">{u.role_name ?? 'No role'}</Badge>,
    },
    {
      key: 'contact', header: 'Contact', className: 'hidden lg:table-cell',
      render: (u) => (
        <span className="block text-xs text-ink-muted">
          {u.email ?? '—'}
          {u.phone && <span className="block text-ink-faint">{u.phone}</span>}
        </span>
      ),
    },
    { key: 'status', header: 'Status', sortable: true, render: (u) => <StatusBadge status={u.status} /> },
    {
      key: 'last_login_at', header: 'Last sign-in', sortable: true, className: 'hidden md:table-cell',
      render: (u) => <span className="text-xs text-ink-muted">{relativeTime(u.last_login_at)}</span>,
    },
    {
      key: 'sessions', header: 'Sessions', className: 'hidden xl:table-cell',
      render: (u) => (u.active_sessions > 0 ? <Badge tone="success">{u.active_sessions} active</Badge> : <span className="text-xs text-ink-faint">—</span>),
    },
    {
      key: 'actions', header: '',
      render: (u) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" aria-label={`Edit ${u.full_name}`} onClick={(e) => { e.stopPropagation(); setEditing(u); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          {can('settings:users') && (
            <Button
              variant="ghost" size="icon"
              aria-label={u.status === 'active' ? `Suspend ${u.full_name}` : `Reactivate ${u.full_name}`}
              onClick={(e) => {
                e.stopPropagation();
                if (u.status === 'active') setSuspending(u);
                else suspend.mutate({ id: u.id, to: 'active' });
              }}
            >
              <ShieldOff className={`h-4 w-4 ${u.status === 'active' ? 'text-red-500' : 'text-emerald-500'}`} />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">Users</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Who can sign in, what they may do, and when they last did.
          </p>
        </div>
        {can('settings:users') && (
          <Button onClick={() => setCreating(true)}><UserPlus className="h-4 w-4" /> New user</Button>
        )}
      </header>

      <Card>
        <div className="flex flex-col gap-2 border-b border-line p-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-ink-faint" aria-hidden />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search name, username, email or employee number…"
              aria-label="Search users"
              className="input-base ps-9"
            />
          </div>
          <Select
            aria-label="Filter by status" value={status} className="sm:w-44"
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="invited">Invited</option>
          </Select>
        </div>

        <DataTable
          columns={columns}
          rows={data?.items ?? []}
          loading={isLoading}
          sort={sort}
          onSort={(key) =>
            setSort((s) => ({ key: key as SortKey, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))
          }
          empty={
            <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-2 text-ink-faint">
                <UsersIcon className="h-6 w-6" />
              </span>
              <p className="text-sm font-semibold text-ink">No users match that search</p>
              <p className="text-sm text-ink-muted">Try a different name, or clear the filters.</p>
            </div>
          }
          footer={data ? <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} /> : undefined}
        />
      </Card>

      {(creating || editing) && (
        <UserForm
          user={editing}
          roles={roles?.roles ?? []}
          onClose={() => { setCreating(false); setEditing(null); }}
        />
      )}

      <ConfirmDialog
        open={!!suspending}
        onClose={() => setSuspending(null)}
        onConfirm={async () => { if (suspending) await suspend.mutateAsync({ id: suspending.id, to: 'suspended' }); }}
        title={`Suspend ${suspending?.full_name ?? ''}?`}
        message="They will be signed out of every device immediately and cannot sign in again until reactivated. Their history stays in the audit log."
        confirmLabel="Suspend user"
      />
    </div>
  );
}

function UserForm({ user, roles, onClose }: { user: UserRow | null; roles: RoleRow[]; onClose: () => void }) {
  const toast = useUi((s) => s.toast);
  const qc = useQueryClient();
  const [form, setForm] = useState({
    fullName: user?.full_name ?? '',
    username: user?.username ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    employeeNo: user?.employee_no ?? '',
    roleId: user?.role_id ?? roles[0]?.id ?? '',
    status: user?.status ?? 'active',
    password: '',
  });
  const [generated, setGenerated] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      user
        ? http.patch(`/users/${user.id}`, { ...form, password: form.password || undefined })
        : http.post<{ generatedPassword?: string }>('/users', { ...form, password: form.password || undefined }),
    onSuccess: (res) => {
      const gen = (res as { generatedPassword?: string })?.generatedPassword;
      if (gen) setGenerated(gen);
      else {
        toast({ kind: 'success', title: user ? 'User updated' : 'User created' });
        void qc.invalidateQueries({ queryKey: ['users'] });
        onClose();
      }
    },
    onError: (err) => toast({ kind: 'error', title: 'Could not save user', message: (err as Error).message }),
  });

  if (generated) {
    return (
      <Modal
        open onClose={onClose} title="Share this password once" size="sm"
        description="It is shown only now and cannot be recovered later."
        footer={<Button onClick={() => { void qc.invalidateQueries({ queryKey: ['users'] }); onClose(); }}>Done</Button>}
      >
        <div className="rounded-lg border border-line bg-surface-0 p-4">
          <p className="font-mono text-lg font-semibold text-ink">{generated}</p>
          <p className="mt-2 text-xs text-ink-muted">
            The user will be asked to choose a new password at their next sign-in.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open onClose={onClose}
      title={user ? `Edit ${user.full_name}` : 'New user'}
      description={user ? `Last sign-in ${dateTime(user.last_login_at)}` : 'They sign in with a username and password.'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            loading={save.isPending}
            onClick={() => save.mutate()}
            disabled={!form.fullName || !form.username || !form.roleId}
          >
            {user ? 'Save changes' : 'Create user'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Full name" required value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          <Input label="Username" required minLength={3} autoComplete="off"
            hint="Letters, numbers, dot, dash and underscore."
            value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          <Input label="Email" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="Employee number" value={form.employeeNo}
            onChange={(e) => setForm({ ...form, employeeNo: e.target.value })} />
          <Select label="Role" required value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name} · {r.permissions.length} permissions</option>
            ))}
          </Select>
        </div>

        <Input
          label={user ? 'Reset password' : 'Password'}
          type="password" autoComplete="new-password"
          hint={user ? 'Leave blank to keep the current password.' : 'Leave blank to generate a one-time password.'}
          minLength={8} value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          leading={<KeyRound className="h-4 w-4" />}
        />

        {user && (
          <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as UserRow['status'] })}>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="invited">Invited</option>
          </Select>
        )}
      </form>
    </Modal>
  );
}
